import { useEffect, useMemo, useState } from "react";
import {
  ScreenWakeLockController,
  browserScreenWakeLock,
} from "../platform/screenWakeLock";

export interface ScreenWakeLockState {
  /** Whether this browser has the API at all. */
  supported: boolean;
  /** Whether a lock is held RIGHT NOW — observed, never assumed. */
  held: boolean;
}

/**
 * Keeps the screen awake for as long as `enabled` is true.
 *
 * Used for kiosk mode (owner, 2026-09-13): a shop tablet that dims mid-shift
 * is a tablet nobody sees. The lock is tied to the ARMED session rather than to
 * any one screen, so it covers the locked PIN screen as well as the floor
 * screens behind it — "kiosk mode" is the whole armed period.
 *
 * The `held` flag is reported rather than inferred, because the platform can
 * refuse (battery saver) and the honest answer then is "not awake", not a
 * silent assumption that the request worked.
 */
export function useScreenWakeLock(enabled: boolean): ScreenWakeLockState {
  const [held, setHeld] = useState(false);
  const [supported] = useState(() => browserScreenWakeLock() !== null);

  useEffect(() => {
    if (!enabled) return;
    const deps = browserScreenWakeLock();
    if (!deps) return;

    const controller = new ScreenWakeLockController({
      ...deps,
      onHeldChange: setHeld,
    });
    void controller.start();

    // The lock is dropped by the browser every time the page is hidden, so
    // this listener is what makes it durable rather than a one-shot.
    const onVisibility = () => void controller.syncVisibility();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      void controller.stop();
      setHeld(false);
    };
  }, [enabled]);

  /*
   * Memoised because this value goes straight into ShopModeContext's own
   * useMemo. A fresh object per render would make that memo useless and
   * re-render every kiosk consumer on every tick.
   */
  return useMemo(() => ({ supported, held }), [supported, held]);
}
